import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ShieldCheck,
  Truck,
  RotateCcw,
  Plus,
  Minus,
  Zap,
  Info,
  Compass,
  ArrowLeft,
  Eye,
  Heart,
  Share2,
  Link2,
  Check,
  Star,
  MessageSquare,
  Send,
  Bookmark,
  Maximize,
} from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useUserStore } from '@/lib/user-store';
import { useTranslation, useCurrency } from '@/hooks/useTranslation';
import { useCartStore, useAppStore } from '@/lib/store';
import toast from 'react-hot-toast';
import ArtworkCard from '@/components/ArtworkCard';
import CustomFrameModal from '@/components/CustomFrameModal';
import ContactInquiryModal from '@/components/ContactInquiryModal';
import ViewInRoomModal from '@/components/ViewInRoomModal';
import type { FrameOption, SizeVariant } from '@/types';

const ALL_FRAME_IDS: FrameOption[] = ['unframed', 'black', 'white', 'custom'];

/* ====== Social share icons (inline SVGs to avoid extra deps) ====== */
const FacebookIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const XIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);
const PinterestIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/></svg>
);
const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);
const TelegramIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
);

const DESC_TRUNCATE = 200;
const ExpandableText: React.FC<{ text: string; limit?: number; className?: string }> = ({ text, limit = DESC_TRUNCATE, className = '' }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text || text.length <= limit) {
    return <p className={className}>{text}</p>;
  }
  return (
    <p className={className}>
      {expanded ? text : `${text.slice(0, limit)}...`}
      <button
        onClick={() => setExpanded(!expanded)}
        className="ml-1 text-[10px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
      >
        {expanded ? 'See less' : 'See more'}
      </button>
    </p>
  );
};

const REVIEW_TRUNCATE = 150;
const ReviewComment: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= REVIEW_TRUNCATE) {
    return <p className="text-sm text-black/60 dark:text-white/60 leading-relaxed">{text}</p>;
  }
  return (
    <p className="text-sm text-black/60 dark:text-white/60 leading-relaxed">
      {expanded ? text : `${text.slice(0, REVIEW_TRUNCATE)}...`}
      <button
        onClick={() => setExpanded(!expanded)}
        className="ml-1 text-[10px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
      >
        {expanded ? 'See less' : 'See more'}
      </button>
    </p>
  );
};

const ArtworkDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, localize } = useTranslation();
  const { formatPrice, formatDual } = useCurrency();
  const addItem = useCartStore((s) => s.addItem);
  const [justAdded, setJustAdded] = useState(false);
  const allArtworks = useAdminStore((s) => s.artworks);
  const allReviews = useAdminStore((s) => s.reviews);
  const addReview = useAdminStore((s) => s.addReview);
  const shareButtons = useAdminStore((s) => s.settings.shareButtons);
  const detailPageTabs = useAdminStore((s) => s.settings.detailPageTabs) ?? { details: true, artist: true, shipping: true, reviews: true };
  const currentUser = useUserStore((s) => s.currentUser);
  const toggleWishlist = useUserStore((s) => s.toggleWishlist);
  const toggleGuestWishlist = useUserStore((s) => s.toggleGuestWishlist);
  const guestWishlist = useUserStore((s) => s.guestWishlist);

  const storeArtwork = allArtworks.find((a) => a.slug === slug);
  const [fetchedArtwork, setFetchedArtwork] = useState<typeof storeArtwork | null>(null);

  // If store artwork has no images, fetch from API (detail endpoint returns full data)
  React.useEffect(() => {
    if (slug && (!storeArtwork?.images || storeArtwork.images.length === 0)) {
      fetch(`/api/v1/artworks/${slug}`)
        .then((r) => r.json())
        .then((d) => { if (d.data) setFetchedArtwork(d.data); })
        .catch(() => {});
    }
  }, [slug, storeArtwork?.images]);

  const artwork = (storeArtwork?.images?.length ? storeArtwork : fetchedArtwork) || storeArtwork;

  // Which frame options the admin has enabled (fall back to all-on for legacy sites).
  const frameOptionsEnabled = useAdminStore((s) => s.settings.frameOptionsEnabled) ?? { unframed: true, black: true, white: true, custom: true };
  const FRAME_IDS = ALL_FRAME_IDS.filter((id) => frameOptionsEnabled[id] !== false);
  const defaultFrame: FrameOption = FRAME_IDS.find((id) => id !== 'custom') ?? FRAME_IDS[0] ?? 'unframed';

  const [currentImg, setCurrentImg] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedFrame, setSelectedFrame] = useState<FrameOption>(defaultFrame);
  // If the currently selected frame gets disabled from admin, snap to the first available.
  useEffect(() => {
    if (!FRAME_IDS.includes(selectedFrame) || selectedFrame === 'custom') {
      setSelectedFrame(defaultFrame);
    }
  }, [FRAME_IDS.join(','), selectedFrame, defaultFrame]);
  const [customFrameOpen, setCustomFrameOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [viewInRoomOpen, setViewInRoomOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'artist' | 'shipping' | 'reviews'>(
    (['details', 'artist', 'shipping', 'reviews'] as const).find((t) => (useAdminStore.getState().settings.detailPageTabs ?? { details: true, artist: true, shipping: true, reviews: true })[t] !== false) ?? 'details'
  );
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewsShown, setReviewsShown] = useState(0);

  // Hooks that were previously after early return — must be at top to keep hook order stable
  const cartItems = useCartStore((s) => s.items);
  const exchangeRateForGel = useAppStore((s) => s.exchangeRate);

  const similarArtworks = useMemo(() => {
    if (!artwork) return [];
    return allArtworks.filter(
      (a) => a.category === artwork.category && a._id !== artwork._id
    ).slice(0, 4);
  }, [artwork]);

  if (!artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <p className="text-black/30 dark:text-white/30">Artwork not found</p>
      </div>
    );
  }

  const images = artwork.images || [];
  const isGiftCard = artwork.categoryId === 'cat_giftcard';
  const artistName =
    typeof artwork.artist === 'string'
      ? artwork.artist
      : localize(artwork.artist.name);

  // Size variants
  const hasVariants = artwork.sizeVariants && artwork.sizeVariants.length > 0;
  const activeVariant: SizeVariant | null = hasVariants
    ? artwork.sizeVariants!.find((v) => v._id === selectedVariant) || null
    : null;

  // Price calculation — prefer per-artwork frameMarkups (percentage), fallback to global frameMultipliers
  const globalFrameMultipliers = useAdminStore.getState().settings.frameMultipliers ?? { black: 1, white: 1, custom: 1 };
  const artworkFrameMarkups = artwork.frameMarkups;
  // Build effective multipliers: artwork markups (in %) override global multipliers
  const frameMultipliers: Record<string, number> = {
    black: artworkFrameMarkups?.black != null && artworkFrameMarkups.black > 0
      ? 1 + (artworkFrameMarkups.black / 100)
      : (globalFrameMultipliers.black || 1),
    white: artworkFrameMarkups?.white != null && artworkFrameMarkups.white > 0
      ? 1 + (artworkFrameMarkups.white / 100)
      : (globalFrameMultipliers.white || 1),
    custom: artworkFrameMarkups?.custom != null && artworkFrameMarkups.custom > 0
      ? 1 + (artworkFrameMarkups.custom / 100)
      : (globalFrameMultipliers.custom || 1),
  };
  const basePrice = activeVariant ? activeVariant.priceCents : artwork.price.amount;
  const frameMultiplier = selectedFrame === 'unframed' ? 1 : (frameMultipliers[selectedFrame] || 1);
  const unitPrice = Math.round(basePrice * frameMultiplier);
  const totalPrice = unitPrice * quantity;
  const sourceCurrency = artwork.price.currency;
  const dualPrice = formatDual(totalPrice, sourceCurrency);
  const unitDualPrice = formatDual(unitPrice, sourceCurrency);

  // Stock calculation
  const maxStock = artwork.rarity === 'unique'
    ? 1
    : artwork.edition
      ? Math.max(0, artwork.edition.total - artwork.edition.current + 1)
      : 99;
  const cartQty = cartItems.filter((i) => i.artwork._id === artwork._id).reduce((sum, i) => sum + i.quantity, 0);
  const availableStock = Math.max(0, maxStock - cartQty);
  const isOutOfStock = availableStock <= 0;

  // High-price → Contact Us instead of Add to Cart (>= 3000 GEL)
  const HIGH_PRICE_GEL_THRESHOLD = 3000;
  const usdAmountDetail = artwork.price.currency === 'GEL' && exchangeRateForGel > 0 ? basePrice / exchangeRateForGel : basePrice;
  const gelPriceDetail = (usdAmountDetail / 100) * (exchangeRateForGel || 2.7);
  const isHighPriceDetail = gelPriceDetail >= HIGH_PRICE_GEL_THRESHOLD;

  // Active dimensions
  const activeDimensions = activeVariant
    ? `${activeVariant.dimensions.width} x ${activeVariant.dimensions.height} ${activeVariant.dimensions.unit}`
    : `${artwork.dimensions.width} x ${artwork.dimensions.height} ${artwork.dimensions.unit}`;

  const next = (): void => setCurrentImg((p) => (p + 1) % images.length);
  const prev = (): void => setCurrentImg((p) => (p - 1 + images.length) % images.length);

  const handleAddToCart = (): void => {
    addItem(artwork, selectedFrame, quantity, activeVariant?._id);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  // Dynamic markup label — show "(+X%)" only when multiplier > 1
  const markupLabel = (mult: number): string => {
    if (!mult || mult <= 1) return '';
    const pct = Math.round((mult - 1) * 100);
    return pct > 0 ? ` (+${pct}%)` : '';
  };
  const frameLabels: Record<FrameOption, string> = {
    unframed: t.artwork.unframed,
    black: t.artwork.blackFrame + markupLabel(frameMultipliers.black ?? 1),
    white: t.artwork.whiteFrame + markupLabel(frameMultipliers.white ?? 1),
    custom: t.artwork.customFrame,
  };

  const tags = [
    artwork.category,
    localize(artwork.medium),
    artwork.sizeClass,
    artwork.rarity !== 'unknown' ? artwork.rarity : null,
    artwork.year && artwork.year > 0 ? artwork.year.toString() : null,
  ].filter(Boolean) as string[];

  // Share helpers
  const pageUrl = window.location.href;
  const shareTitle = localize(artwork.title);

  const handleCopyLink = (): void => {
    navigator.clipboard.writeText(pageUrl);
    setLinkCopied(true);
    toast.success(t.artwork.linkCopied);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const shareLinks = [
    { key: 'facebook' as const, icon: <FacebookIcon size={16} />, label: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}` },
    { key: 'twitter' as const, icon: <XIcon size={14} />, label: 'X', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareTitle)}` },
    { key: 'pinterest' as const, icon: <PinterestIcon size={16} />, label: 'Pinterest', url: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pageUrl)}&media=${encodeURIComponent(images[0] || '')}&description=${encodeURIComponent(shareTitle)}` },
    { key: 'whatsapp' as const, icon: <WhatsAppIcon size={16} />, label: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + pageUrl)}` },
    { key: 'telegram' as const, icon: <TelegramIcon size={16} />, label: 'Telegram', url: `https://t.me/share/url?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareTitle)}` },
  ];

  const visibleShareLinks = shareLinks.filter((s) => shareButtons?.[s.key]);
  const showCopyLink = shareButtons?.copyLink !== false;
  const hasAnyShare = visibleShareLinks.length > 0 || showCopyLink;

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-4">
        <button
          onClick={() => navigate('/shop')}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
        >
          <ArrowLeft size={14} />
          {t.artwork.backToShop}
        </button>
      </div>

      {/* Main Product Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">

          {/* Image Gallery */}
          <div className="space-y-3">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden">
              <div className="relative w-full">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImg}
                    src={images[currentImg]}
                    alt={localize(artwork.title)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="w-full h-auto rounded-2xl"
                  />
                </AnimatePresence>
              </div>

              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}

              {/* Slide counter */}
              {images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImg(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        currentImg === i
                          ? 'bg-white w-6 shadow-md'
                          : 'bg-white/40 w-1.5 hover:bg-white/60'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* View count */}
              <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-full text-white text-[9px] font-medium">
                <Eye size={10} />
                {artwork.viewCount}
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImg(i)}
                    className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      currentImg === i
                        ? 'border-black dark:border-white ring-2 ring-black/10 dark:ring-white/10'
                        : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* View in Room Button */}
            <motion.button
              type="button"
              onClick={() => setViewInRoomOpen(true)}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="group relative w-full mt-2 py-4 bg-gradient-to-r from-black via-black/95 to-black dark:from-white dark:via-white/95 dark:to-white text-white dark:text-black rounded-2xl overflow-hidden flex items-center justify-center gap-3 shadow-lg hover:shadow-2xl transition-shadow"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent dark:via-black/10 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              <Maximize size={16} className="relative z-10" />
              <span className="relative z-10 text-[10px] font-bold uppercase tracking-[0.3em]">
                {t.artwork?.viewInRoom || 'View in Room'}
              </span>
            </motion.button>

          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            {/* Title & Artist */}
            <div className="space-y-3 mb-6">
              {artwork.seriesName && (localize(artwork.seriesName)?.trim()) && (
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-black/40 dark:text-white/40">
                  {localize(artwork.seriesName)}
                </p>
              )}
              <h1 className="text-3xl sm:text-4xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tight leading-tight">
                {localize(artwork.title)}
              </h1>
              <p className="text-base text-black/40 dark:text-white/40 font-light">
                {t.artwork.by}{' '}
                {typeof artwork.artist !== 'string' ? (
                  <Link
                    to={`/artists/${artwork.artist.slug}`}
                    className="text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white underline underline-offset-4 transition-colors"
                  >
                    {artistName}
                  </Link>
                ) : (
                  artistName
                )}
                <button
                  onClick={() => {
                    const url = window.location.href;
                    if (navigator.share) {
                      navigator.share({ title: localize(artwork.title), text: `${localize(artwork.title)} — ${artistName}`, url });
                    } else {
                      navigator.clipboard.writeText(url);
                      toast.success(t.nav.home === 'Home' ? 'Link copied!' : 'ლინკი დაკოპირდა!');
                    }
                  }}
                  className="w-8 h-8 rounded-full bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center hover:bg-black/[0.08] dark:hover:bg-white/[0.1] transition-all ml-2 shrink-0"
                  title="Share"
                >
                  <Share2 size={14} className="text-black/40 dark:text-white/40" />
                </button>
              </p>
            </div>

            {/* Price & Status — Dual Currency (unit price) */}
            <div className="flex items-baseline gap-4 mb-6 flex-wrap">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-syncopate font-bold">{unitDualPrice.primary}</span>
                <span className="text-sm font-medium text-black/25 dark:text-white/25">{unitDualPrice.secondary}</span>
              </div>
              {artwork.isForSale && !isOutOfStock && (
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-3 py-1 rounded-full">
                  <Zap size={10} fill="currentColor" />
                  <span className="text-[9px] font-bold uppercase tracking-widest">
                    {t.artwork.inStock} ({availableStock})
                  </span>
                </div>
              )}
              {isOutOfStock && (
                <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 px-3 py-1 rounded-full">
                  <Info size={10} />
                  <span className="text-[9px] font-bold uppercase tracking-widest">
                    {t.artwork.outOfStock || 'Out of Stock'}
                  </span>
                </div>
              )}
              {artwork.edition && (
                <span className="text-[10px] text-black/40 dark:text-white/40 font-medium">
                  {t.artwork.edition}: {artwork.edition.current}/{artwork.edition.total}
                </span>
              )}
            </div>

            {/* Tags */}
            {!isGiftCard && (
            <div className="flex flex-wrap gap-2 mb-8">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-black/[0.04] dark:bg-white/[0.06] border border-black/[0.06] dark:border-white/[0.06] rounded-full text-[10px] font-medium text-black/60 dark:text-white/60 tracking-wide capitalize"
                >
                  {tag}
                </span>
              ))}
            </div>
            )}

            {/* Size / Amount Variant Selector */}
            {hasVariants && (
              <div className="space-y-3 mb-6">
                <span className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                  {isGiftCard ? t.giftCard.selectAmount : t.artwork.selectSize}
                </span>
                <div className="relative">
                  <button
                    onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                    className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border border-black/[0.08] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.02] hover:border-black/15 dark:hover:border-white/15 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-black/[0.05] dark:bg-white/[0.05] flex items-center justify-center">
                        {isGiftCard ? (
                          <span className="text-[11px] font-bold text-black/40 dark:text-white/40">$</span>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-black/40 dark:text-white/40"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></svg>
                        )}
                      </div>
                      {activeVariant ? (
                        <div className="text-left">
                          <p className="text-xs font-bold">{localize(activeVariant.label)}</p>
                          {!isGiftCard && (
                            <p className="text-[10px] text-black/40 dark:text-white/40">
                              {activeVariant.dimensions.width} x {activeVariant.dimensions.height} {activeVariant.dimensions.unit} — {formatDual(activeVariant.priceCents).primary}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-black/40 dark:text-white/40">{isGiftCard ? t.giftCard.selectAmount : t.artwork.selectSize}</span>
                      )}
                    </div>
                    <motion.div animate={{ rotate: sizeDropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronDown size={16} className="text-black/30 dark:text-white/30" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {sizeDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 right-0 mt-2 z-50 bg-white dark:bg-[#111] border border-black/[0.08] dark:border-white/[0.08] rounded-2xl shadow-xl overflow-hidden"
                      >
                        {/* Default (original) option */}
                        <button
                          onClick={() => { setSelectedVariant(null); setSizeDropdownOpen(false); }}
                          className={`w-full flex items-center justify-between px-4 py-3.5 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-all ${
                            !selectedVariant ? 'bg-black/[0.04] dark:bg-white/[0.04]' : ''
                          }`}
                        >
                          <div className="text-left">
                            <p className="text-xs font-bold">{isGiftCard ? formatDual(artwork.price.amount, artwork.price.currency).primary : t.artwork.dimensions}</p>
                            {!isGiftCard && (
                              <p className="text-[10px] text-black/40 dark:text-white/40">
                                {artwork.dimensions.width} x {artwork.dimensions.height} {artwork.dimensions.unit}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-bold">{formatDual(artwork.price.amount, artwork.price.currency).primary}</p>
                            <p className="text-[9px] text-black/25 dark:text-white/25">{formatDual(artwork.price.amount, artwork.price.currency).secondary}</p>
                          </div>
                        </button>

                        {artwork.sizeVariants!.map((variant) => (
                          <button
                            key={variant._id}
                            onClick={() => { setSelectedVariant(variant._id); setSizeDropdownOpen(false); }}
                            className={`w-full flex items-center justify-between px-4 py-3.5 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-all border-t border-black/[0.04] dark:border-white/[0.04] ${
                              selectedVariant === variant._id ? 'bg-black/[0.04] dark:bg-white/[0.04]' : ''
                            }`}
                          >
                            <div className="text-left">
                              <p className="text-xs font-bold">{localize(variant.label)}</p>
                              {!isGiftCard && (
                                <p className="text-[10px] text-black/40 dark:text-white/40">
                                  {variant.dimensions.width} x {variant.dimensions.height} {variant.dimensions.unit}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-bold">{formatDual(variant.priceCents, artwork.price.currency).primary}</p>
                              <p className="text-[9px] text-black/25 dark:text-white/25">{formatDual(variant.priceCents, artwork.price.currency).secondary}</p>
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* Tabs — hidden for gift cards */}
            {!isGiftCard && (
            <>
            <nav className="flex gap-6 border-b border-black/5 dark:border-white/5 mb-6 pb-3">
              {([
                { id: 'details' as const, label: t.artwork.aboutWork, icon: <Info size={13} /> },
                { id: 'artist' as const, label: t.artwork.aboutArtist, icon: <Compass size={13} /> },
                { id: 'shipping' as const, label: t.artwork.shipping, icon: <Truck size={13} /> },
                { id: 'reviews' as const, label: t.reviews.title, icon: <MessageSquare size={13} /> },
              ]).filter((tab) => detailPageTabs[tab.id] !== false).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-all relative pb-1 ${
                    activeTab === tab.id
                      ? 'text-black dark:text-white'
                      : 'text-black/30 dark:text-white/30'
                  }`}
                >
                  {tab.icon} {tab.label}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="detail-tab"
                      className="absolute -bottom-[13px] left-0 right-0 h-0.5 bg-black dark:bg-white"
                    />
                  )}
                </button>
              ))}
            </nav>

            {/* Tab Content */}
            <div className="mb-8 min-h-[140px]">
              {activeTab === 'details' && (
                <div className="space-y-5">
                  <ExpandableText
                    text={localize(artwork.description)}
                    className="text-black/60 dark:text-white/60 text-sm leading-relaxed whitespace-pre-line text-justify"
                  />
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    {[
                      { label: t.artwork.medium, value: localize(artwork.medium) },
                      { label: t.artwork.dimensions, value: activeDimensions },
                      artwork.year && artwork.year > 0 ? { label: t.artwork.year, value: artwork.year } : null,
                      { label: t.artwork.rarity, value: artwork.rarity },
                    ].filter((x): x is NonNullable<typeof x> => x !== null).map((item) => (
                      <div key={item.label} className="py-2 border-b border-black/5 dark:border-white/5">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-black/30 dark:text-white/30 block mb-0.5">
                          {item.label}
                        </span>
                        <p className="text-xs font-semibold capitalize">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'artist' && (
                <div className="space-y-4">
                  {typeof artwork.artist !== 'string' && (
                    <>
                      <div className="flex items-center gap-4">
                        {artwork.artist.portrait ? (
                          <img
                            src={artwork.artist.portrait}
                            alt={localize(artwork.artist.name)}
                            className="w-14 h-14 rounded-full object-cover ring-2 ring-black/5 dark:ring-white/5"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }}
                          />
                        ) : null}
                        <div className={`w-14 h-14 rounded-full bg-black/[0.06] dark:bg-white/[0.06] ring-2 ring-black/5 dark:ring-white/5 flex items-center justify-center text-lg font-bold font-syncopate text-black/40 dark:text-white/40 ${artwork.artist.portrait ? 'hidden' : ''}`}>
                          {localize(artwork.artist.name).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold">{localize(artwork.artist.name)}</h4>
                          <p className="text-[10px] text-black/40 dark:text-white/40">
                            {artwork.artist.artworkCount} {t.artists.works}
                          </p>
                        </div>
                      </div>
                      <p className="text-black/60 dark:text-white/60 text-sm leading-relaxed whitespace-pre-line text-justify">
                        {localize(artwork.artist.bio)}
                      </p>
                      <Link
                        to={`/artists/${artwork.artist.slug}`}
                        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white transition-colors"
                      >
                        {t.artists.seeMore} <ChevronRight size={12} />
                      </Link>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'shipping' && (
                <div className="space-y-3">
                  {[
                    { icon: <ShieldCheck size={18} />, text: t.artwork.authentication },
                    { icon: <Truck size={18} />, text: t.artwork.shippingInfo },
                    { icon: <RotateCcw size={18} />, text: t.artwork.returnInfo },
                  ].map((item) => (
                    <div
                      key={item.text}
                      className="flex items-center gap-4 p-4 rounded-xl border border-black/5 dark:border-white/5"
                    >
                      <div className="w-9 h-9 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center shrink-0 text-black/40 dark:text-white/40">
                        {item.icon}
                      </div>
                      <span className="text-xs font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="space-y-6">
                  {/* Reviews Slider — one at a time with arrows */}
                  {(() => {
                    const artworkReviews = allReviews
                      .filter((r) => r.artworkId === artwork._id && !r.isHidden)
                      .sort((a, b) => b.rating - a.rating || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                    if (artworkReviews.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <MessageSquare size={32} className="mx-auto text-black/10 dark:text-white/10 mb-3" />
                          <p className="text-sm text-black/40 dark:text-white/40">{t.reviews.noReviews}</p>
                          <p className="text-xs text-black/25 dark:text-white/25 mt-1">{t.reviews.beFirst}</p>
                        </div>
                      );
                    }
                    const review = artworkReviews[reviewsShown % artworkReviews.length]!;
                    return (
                      <div>
                        {/* Header: count + arrows */}
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                            {artworkReviews.length} {artworkReviews.length === 1 ? 'Review' : 'Reviews'}
                          </span>
                          {artworkReviews.length > 1 && (
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-bold text-black/30 dark:text-white/30">
                                {(reviewsShown % artworkReviews.length) + 1} / {artworkReviews.length}
                              </span>
                              <button
                                onClick={() => setReviewsShown((prev) => (prev - 1 + artworkReviews.length) % artworkReviews.length)}
                                className="w-8 h-8 rounded-full border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/40 dark:text-white/40 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
                              >
                                <ChevronLeft size={14} />
                              </button>
                              <button
                                onClick={() => setReviewsShown((prev) => (prev + 1) % artworkReviews.length)}
                                className="w-8 h-8 rounded-full border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/40 dark:text-white/40 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
                              >
                                <ChevronRight size={14} />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Single review card */}
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={review._id}
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -30 }}
                            transition={{ duration: 0.25 }}
                            className="p-5 rounded-xl border border-black/5 dark:border-white/5 space-y-3"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold">{review.authorName}</span>
                              <span className="text-[9px] text-black/30 dark:text-white/30">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  size={14}
                                  className={star <= review.rating ? 'text-amber-400' : 'text-black/10 dark:text-white/10'}
                                  fill={star <= review.rating ? 'currentColor' : 'none'}
                                />
                              ))}
                            </div>
                            <ReviewComment text={review.comment} />
                          </motion.div>
                        </AnimatePresence>

                        {/* Dots */}
                        {artworkReviews.length > 1 && (
                          <div className="flex justify-center mt-4 gap-1.5">
                            {artworkReviews.map((_, i) => (
                              <button
                                key={i}
                                onClick={() => setReviewsShown(i)}
                                className={`w-1.5 h-1.5 rounded-full transition-all ${
                                  i === reviewsShown % artworkReviews.length
                                    ? 'bg-black dark:bg-white w-4'
                                    : 'bg-black/15 dark:bg-white/15'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Write Review Form */}
                  {currentUser ? (
                    <div className="p-4 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.02] space-y-4">
                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                        {t.reviews.writeReview}
                      </h4>
                      {/* Star Rating */}
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="transition-transform hover:scale-110"
                          >
                            <Star
                              size={20}
                              className={
                                star <= (hoverRating || reviewRating)
                                  ? 'text-amber-400'
                                  : 'text-black/15 dark:text-white/15'
                              }
                              fill={star <= (hoverRating || reviewRating) ? 'currentColor' : 'none'}
                            />
                          </button>
                        ))}
                      </div>
                      {/* Comment */}
                      <div className="relative">
                        <textarea
                          value={reviewComment}
                          onChange={(e) => { if (e.target.value.length <= 500) setReviewComment(e.target.value); }}
                          placeholder={t.reviews.comment}
                          rows={3}
                          maxLength={500}
                          className="w-full px-4 py-3 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-white dark:bg-black text-sm placeholder:text-black/30 dark:placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10 resize-none"
                        />
                        <span className={`absolute bottom-2 right-3 text-[9px] font-bold ${reviewComment.length > 450 ? 'text-red-400' : 'text-black/20 dark:text-white/20'}`}>
                          {reviewComment.length}/500
                        </span>
                      </div>
                      {/* Submit */}
                      <button
                        onClick={() => {
                          if (reviewRating === 0 || !reviewComment.trim()) return;
                          addReview({
                            artworkId: artwork._id,
                            userId: currentUser._id,
                            authorName: `${currentUser.firstName} ${currentUser.lastName}`,
                            rating: reviewRating,
                            comment: reviewComment.trim(),
                            isHidden: false,
                          });
                          setReviewRating(0);
                          setReviewComment('');
                          toast.success(t.reviews.submitted);
                        }}
                        disabled={reviewRating === 0 || !reviewComment.trim()}
                        className="flex items-center gap-2 px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-black/90 dark:hover:bg-white/90 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <Send size={12} />
                        {t.reviews.submit}
                      </button>
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl border border-dashed border-black/10 dark:border-white/10 text-center">
                      <p className="text-xs text-black/40 dark:text-white/40">{t.reviews.loginToReview}</p>
                      <Link
                        to="/login"
                        className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white underline underline-offset-4 transition-colors"
                      >
                        {t.auth.login}
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
            )}

            {/* Gift card description — shown instead of tabs */}
            {isGiftCard && (
              <div className="space-y-6 mb-8">
                <p className="text-black/60 dark:text-white/60 text-sm leading-relaxed whitespace-pre-line text-justify">
                  {localize(artwork.description)}
                </p>
                {/* Reviews for gift cards */}
                <div className="pt-4 border-t border-black/5 dark:border-white/5">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 mb-4 flex items-center gap-1.5">
                    <MessageSquare size={13} /> {t.reviews.title}
                  </h3>
                  {(() => {
                    const artworkReviews = allReviews.filter((r) => r.artworkId === artwork._id && !r.isHidden);
                    if (artworkReviews.length === 0) {
                      return (
                        <div className="text-center py-6">
                          <p className="text-xs text-black/30 dark:text-white/30">{t.reviews.noReviews}</p>
                        </div>
                      );
                    }
                    return artworkReviews.map((review) => (
                      <div key={review._id} className="p-3 rounded-xl border border-black/5 dark:border-white/5 space-y-1.5 mb-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold">{review.authorName}</span>
                          <span className="text-[9px] text-black/30 dark:text-white/30">{new Date(review.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} size={12} className={star <= review.rating ? 'text-amber-400' : 'text-black/10 dark:text-white/10'} fill={star <= review.rating ? 'currentColor' : 'none'} />
                          ))}
                        </div>
                        <p className="text-xs text-black/60 dark:text-white/60">{review.comment}</p>
                      </div>
                    ));
                  })()}
                  {currentUser ? (
                    <div className="mt-4 space-y-3">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button key={star} type="button" onClick={() => setReviewRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)}>
                            <Star size={18} className={star <= (hoverRating || reviewRating) ? 'text-amber-400' : 'text-black/15 dark:text-white/15'} fill={star <= (hoverRating || reviewRating) ? 'currentColor' : 'none'} />
                          </button>
                        ))}
                      </div>
                      <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder={t.reviews.comment} rows={2} className="w-full px-3 py-2 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-white dark:bg-black text-sm placeholder:text-black/30 dark:placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10 resize-none" />
                      <button
                        onClick={() => {
                          if (reviewRating === 0 || !reviewComment.trim()) return;
                          addReview({ artworkId: artwork._id, userId: currentUser._id, authorName: `${currentUser.firstName} ${currentUser.lastName}`, rating: reviewRating, comment: reviewComment.trim(), isHidden: false });
                          setReviewRating(0); setReviewComment(''); toast.success(t.reviews.submitted);
                        }}
                        disabled={reviewRating === 0 || !reviewComment.trim()}
                        className="flex items-center gap-2 px-4 py-2 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-widest rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <Send size={12} /> {t.reviews.submit}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3 text-center">
                      <Link to="/login" className="text-[10px] font-bold uppercase tracking-widest text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white underline underline-offset-4 transition-colors">
                        {t.reviews.loginToReview}
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Frame Selection — hidden for gift cards and non-details tabs; also hidden if no frame options are enabled. */}
            {!isGiftCard && activeTab === 'details' && FRAME_IDS.length > 0 && (
            <div className="space-y-3 mb-6">
              <span className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                {t.artwork.frame}
              </span>
              <div className={`grid gap-2 ${FRAME_IDS.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {FRAME_IDS.map((frameId) => (
                  <button
                    key={frameId}
                    onClick={() => {
                      if (frameId === 'custom') {
                        setCustomFrameOpen(true);
                      } else {
                        setSelectedFrame(frameId);
                      }
                    }}
                    className={`px-3 py-2.5 rounded-xl border text-[9px] font-bold uppercase tracking-widest transition-all ${
                      frameId === 'custom'
                        ? 'border-dashed border-black/20 dark:border-white/20 text-black/60 dark:text-white/60 hover:border-black/40 dark:hover:border-white/40'
                        : selectedFrame === frameId
                          ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                          : 'border-black/5 dark:border-white/5 text-black dark:text-white hover:border-black/20 dark:hover:border-white/20'
                    }`}
                  >
                    {frameLabels[frameId]}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="space-y-4 pt-6 border-t border-black/5 dark:border-white/5 mt-auto">
              <div className="flex items-center justify-between">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                    {t.artwork.quantity}
                  </span>
                  <div className="flex items-center gap-4 bg-black/5 dark:bg-white/5 p-1.5 rounded-full">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      className="w-8 h-8 bg-white dark:bg-black rounded-full flex items-center justify-center hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all shadow-sm disabled:opacity-30 disabled:pointer-events-none"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="text-sm font-bold font-syncopate min-w-[2ch] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity((q) => Math.min(availableStock, q + 1))}
                      disabled={quantity >= availableStock}
                      className="w-8 h-8 bg-white dark:bg-black rounded-full flex items-center justify-center hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all shadow-sm disabled:opacity-30 disabled:pointer-events-none"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 block mb-1">
                    {t.artwork.total}
                  </span>
                  <span className="text-2xl font-syncopate font-bold">{dualPrice.primary}</span>
                  <span className="block text-xs text-black/25 dark:text-white/25">{dualPrice.secondary}</span>
                </div>
              </div>

              {isOutOfStock && (
                <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="px-4 py-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 dark:text-red-400 text-center">
                    {t.artwork.outOfStock || 'This item is currently out of stock'}
                  </p>
                </motion.div>
              )}
              <div className="flex gap-3">
                {isHighPriceDetail ? (
                  <button
                    type="button"
                    onClick={() => setContactOpen(true)}
                    className="flex-1 py-5 text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl active:scale-[0.98] transition-all shadow-xl flex items-center justify-center gap-3 bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
                  >
                    Contact Us <ChevronRight size={16} />
                  </button>
                ) : (
                  <motion.button
                    onClick={handleAddToCart}
                    disabled={isOutOfStock}
                    animate={justAdded ? { scale: [1, 1.03, 1] } : {}}
                    transition={{ duration: 0.3 }}
                    className={`flex-1 py-5 text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl active:scale-[0.98] transition-all shadow-xl flex items-center justify-center gap-3 disabled:opacity-40 disabled:pointer-events-none ${
                      justAdded
                        ? 'bg-emerald-600 dark:bg-emerald-500 text-white'
                        : 'bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90'
                    }`}
                  >
                    <AnimatePresence mode="wait">
                      {justAdded ? (
                        <motion.span key="added" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.5, opacity: 0 }} transition={{ duration: 0.2 }} className="flex items-center gap-2">
                          <Check size={16} strokeWidth={3} /> {t.shop.addedToCart || 'Added to Cart'}
                        </motion.span>
                      ) : (
                        <motion.span key="add" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.5, opacity: 0 }} transition={{ duration: 0.2 }} className="flex items-center gap-2">
                          {t.shop.addToCart} <ChevronRight size={16} />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.button>
                )}
                <button
                  onClick={() => {
                    if (currentUser) {
                      toggleWishlist(artwork._id);
                    } else {
                      toggleGuestWishlist(artwork._id);
                    }
                  }}
                  className={`w-14 h-14 rounded-2xl border flex items-center justify-center transition-all shrink-0 ${
                    (currentUser ? currentUser.wishlist.includes(artwork._id) : guestWishlist.includes(artwork._id))
                      ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-red-500'
                      : 'border-black/10 dark:border-white/10 text-black/30 dark:text-white/30 hover:text-red-500 hover:border-red-200 dark:hover:border-red-500/20'
                  }`}
                >
                  <Heart size={20} fill={(currentUser ? currentUser.wishlist.includes(artwork._id) : guestWishlist.includes(artwork._id)) ? 'currentColor' : 'none'} />
                </button>
              </div>

              {/* Social Share */}
              {hasAnyShare && (
                <div className="pt-4">
                  <div className="relative">
                    <button
                      onClick={() => setShareOpen(!shareOpen)}
                      className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                    >
                      <Share2 size={14} />
                      {t.artwork.share}
                    </button>

                    <AnimatePresence>
                      {shareOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                          className="absolute bottom-full left-0 mb-3 flex items-center gap-1.5 p-2 bg-white dark:bg-[#111] border border-black/[0.06] dark:border-white/[0.08] rounded-2xl shadow-xl z-50"
                        >
                          {visibleShareLinks.map((s) => (
                            <a
                              key={s.key}
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={s.label}
                              className="w-9 h-9 rounded-xl flex items-center justify-center text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white hover:bg-black/[0.05] dark:hover:bg-white/[0.05] transition-all"
                            >
                              {s.icon}
                            </a>
                          ))}
                          {showCopyLink && (
                            <button
                              onClick={handleCopyLink}
                              title="Copy link"
                              className="w-9 h-9 rounded-xl flex items-center justify-center text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white hover:bg-black/[0.05] dark:hover:bg-white/[0.05] transition-all"
                            >
                              {linkCopied ? <Check size={15} className="text-green-500" /> : <Link2 size={15} />}
                            </button>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Similar Products */}
      {similarArtworks.length > 0 && (
        <section className="border-t border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#050505] transition-colors duration-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg sm:text-xl font-syncopate font-bold uppercase tracking-tight">
                {t.artwork.similarWorks}
              </h2>
              <Link
                to="/shop"
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
              >
                {t.artists.seeMore} <ChevronRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {similarArtworks.map((a) => (
                <ArtworkCard key={a._id} artwork={a} />
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Custom Frame Modal */}
      <CustomFrameModal
        isOpen={customFrameOpen}
        onClose={() => setCustomFrameOpen(false)}
        artwork={artwork}
      />
      {/* Contact Inquiry Modal — for high-priced artworks */}
      <ContactInquiryModal
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
        artwork={artwork}
      />
      {/* View in Room Modal */}
      <ViewInRoomModal
        isOpen={viewInRoomOpen}
        onClose={() => setViewInRoomOpen(false)}
        artwork={artwork}
      />
    </div>
  );
};

export default ArtworkDetailPage;
